import { put, takeEvery, take, fork, call, select, actionChannel, all, cancel, spawn, } from 'redux-saga/effects';
import { channel } from 'redux-saga';
import { subscribe, unsubscribe } from '@push-component/client';
import { BYO_PRICE_UPDATES } from '@push-component/constants';
import { noticeError } from '../../../utils/noticeError';
import * as constants from '../../constants';
import { addPrice, addSelectionIncompatibleError, addSelectionToProcessingQueue, addSelectionToStore, expandSlip, hideEventLevelError, marketFetchingStarted, processSelection, removePrice, removeSelectionFromProcessingQueue, removeSelectionFromStore, saveMarkets, setEditID, setLastTopic, showEventLevelError, updatePrice, addExpiredSelections, setCompetitionId, } from '../../actions';
import { publishSelectionAddAnalytics, publishSelectionRemoveAnalytics } from '../../../components/analyticsHelpers';
import config from '../../../config';
import { getMarketsData, getPrice } from '../../../dataProviders';
import { PUSH_SUBSCRIBE_OWNER } from '../../../constants';
import { areSelectionsIncompatible, matchSelections } from './utils';
const subscribeChannel = channel();
let updateTask;
export function* callPrice(selections, eventId, force) {
    const gtpEventId = yield eventId || select(state => { var _a, _b; return (_b = (_a = state.markets) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.id; });
    const lastTopic = yield select(state => state.slip.lastTopic);
    const slipSelections = yield selections || select(state => Object.keys(state.slip.selections)
        .map(key => state.slip.selections[key]));
    yield call(cancelUpdateTask);
    if (gtpEventId && (!slipSelections.some((selection) => selection.active === false) || force)) {
        const sportId = yield select(state => state.markets.data.sportId);
        const price = yield call(getPrice, slipSelections, gtpEventId, sportId, lastTopic);
        if (price) {
            yield put(addPrice(price.selection));
            updateTask = yield spawn(updatePriceSaga, price.priceSubscription);
        }
    }
}
export function* cancelUpdateTask() {
    const topic = yield select(state => state.slip.lastTopic);
    if (topic) {
        unsubscribe({
            topic,
            owner: PUSH_SUBSCRIBE_OWNER,
            type: BYO_PRICE_UPDATES
        });
    }
    if (updateTask && !updateTask.isCancelled()) {
        yield cancel(updateTask);
    }
}
function* checkPriceHasChanged(price) {
    const oldPrice = yield select(state => state.slip.prices);
    const priceHasChanged = (oldPrice === null || oldPrice === void 0 ? void 0 : oldPrice.type) !== (price === null || price === void 0 ? void 0 : price.type) || (oldPrice === null || oldPrice === void 0 ? void 0 : oldPrice.numerator) !== (price === null || price === void 0 ? void 0 : price.numerator)
        || (oldPrice === null || oldPrice === void 0 ? void 0 : oldPrice.denominator) !== (price === null || price === void 0 ? void 0 : price.denominator) || (oldPrice === null || oldPrice === void 0 ? void 0 : oldPrice.us) !== (price === null || price === void 0 ? void 0 : price.us)
        || (oldPrice === null || oldPrice === void 0 ? void 0 : oldPrice.decimal) !== (price === null || price === void 0 ? void 0 : price.decimal);
    return Boolean(!oldPrice || priceHasChanged);
}
export function* updatePriceSaga(priceSubscription) {
    var _a, _b;
    const topic = (priceSubscription === null || priceSubscription === void 0 ? void 0 : priceSubscription.topic) ? priceSubscription.topic : null;
    if (!topic) {
        return;
    }
    yield put(setLastTopic(topic));
    subscribe({
        topic,
        owner: PUSH_SUBSCRIBE_OWNER,
        type: BYO_PRICE_UPDATES,
        callback: subscribeChannel.put
    });
    while (true) {
        const action = yield take(subscribeChannel);
        const priceHasChanged = yield checkPriceHasChanged((_a = action.data) === null || _a === void 0 ? void 0 : _a.price);
        const pricePayload = priceHasChanged ? Object.assign(Object.assign({}, action.data), { price: Object.assign(Object.assign({}, (_b = action.data) === null || _b === void 0 ? void 0 : _b.price), { updateTimestamp: Date.now() }) }) : action.data;
        yield put(updatePrice(pricePayload));
    }
}
export function* processSelectionSaga(action) {
    const selectionToAdd = action.payload;
    try {
        const selectionsInSlip = yield select(state => Object.keys(state.slip.selections)
            .map(key => state.slip.selections[key]));
        const incompatibleSelections = selectionsInSlip.filter((sel) => areSelectionsIncompatible(selectionToAdd, sel));
        const newSelections = selectionsInSlip
            .filter((sel) => !incompatibleSelections.some((incompatible) => incompatible.id === sel.id))
            .concat(selectionToAdd);
        if (newSelections && newSelections.length >= config.minimumSelections) {
            yield call(callPrice, newSelections, undefined, true);
        }
        const sportId = yield select(state => state.markets.data.sportId);
        yield put(addSelectionToStore(selectionToAdd, sportId));
        if (incompatibleSelections.length > 0) {
            yield all(incompatibleSelections.map((sel) => put(removeSelectionFromStore(sel))));
        }
        yield put(removeSelectionFromProcessingQueue(selectionToAdd));
        publishSelectionAddAnalytics(selectionToAdd);
    }
    catch (error) {
        // NewRelic monitoring
        noticeError(error, { errorType: constants.BYO_ERROR_BUILDSLIP });
        yield put(removeSelectionFromProcessingQueue(selectionToAdd));
        yield put(addSelectionIncompatibleError(error));
        yield call(cancelUpdateTask);
    }
}
export function* addSelectionSaga(action) {
    const selectionsInSlip = yield select(state => Object.keys(state.slip.selections).length);
    const selectionInProcess = yield select(state => state.slip.processingSelections.length);
    const sportId = yield select(state => state.markets.data.sportId);
    if (selectionsInSlip + selectionInProcess < config.maximumLegPartsBySport(sportId)) {
        yield put(addSelectionToProcessingQueue(action.payload));
        yield put(processSelection(action.payload));
    }
}
export function* removeSelectionSaga(action) {
    try {
        yield put(removeSelectionFromStore(action.payload));
        const newSelections = yield select(state => Object.keys(state.slip.selections).filter(id => id !== action.payload.id).map(id => state.slip.selections[id]));
        yield call(cancelUpdateTask);
        if (newSelections && newSelections.length >= config.minimumSelections) {
            yield call(callPrice, newSelections, undefined, true);
        }
        else if (newSelections.length === 1) {
            yield put(removePrice());
        }
        else {
            yield put(removePrice());
            yield put(expandSlip(false));
        }
        publishSelectionRemoveAnalytics(action.payload);
    }
    catch (error) {
        // NewRelic monitoring
        noticeError(error, { errorType: constants.BYO_ERROR_BUILDSLIP });
        yield put(addSelectionIncompatibleError(error));
        yield call(cancelUpdateTask);
    }
}
export function* processSelectionWatcher() {
    const channelQueue = yield actionChannel(constants.BYO_PROCESS_SELECTION);
    while (true) {
        const action = yield take(channelQueue);
        yield call(processSelectionSaga, action);
    }
}
export function* editSelectionsSaga(action) {
    var _a;
    try {
        const { data, id, eventId } = action.payload;
        const { byoSelectionDetails } = data;
        yield put(hideEventLevelError());
        yield put(marketFetchingStarted());
        const marketsResponse = yield call(getMarketsData, config.locale, eventId, true);
        yield put(saveMarkets(marketsResponse));
        yield put(setCompetitionId(((_a = marketsResponse.ancestors) === null || _a === void 0 ? void 0 : _a[0]) || null));
        let selectionsIds = new Set(byoSelectionDetails.map(({ selectionId, handicap }) => handicap ? `${selectionId}_${handicap.toFixed(2).replace('.', '-')}` : selectionId));
        let selectedSelections = [];
        [selectedSelections, selectionsIds] = matchSelections(selectionsIds, selectedSelections, marketsResponse.byoMarketGroups);
        // fixing hook for 400 from getByoPrice
        if (selectionsIds.size) {
            const updates = yield select(state => state.markets.updates[eventId]);
            [selectedSelections, selectionsIds] = matchSelections(selectionsIds, selectedSelections, updates === null || updates === void 0 ? void 0 : updates.byoMarketGroups);
        }
        if (selectionsIds.size) {
            // there are still selections we didn't match with markets nor Diffusion data
            // we need to handle them separately - remove it from store, let the user know by message
            const expiredSelection = [...selectionsIds];
            yield put(addExpiredSelections(expiredSelection));
        }
        const { sportId } = marketsResponse;
        yield all(selectedSelections.map(selection => put(addSelectionToStore(selection, sportId))));
        yield call(callPrice, selectedSelections, marketsResponse.id);
        yield put(setEditID(id));
    }
    catch (error) {
        yield put(showEventLevelError(error.toString()));
        yield call(cancelUpdateTask);
    }
}
export function* clearAllSaga() {
    yield call(cancelUpdateTask);
}
export function* enableUpdateTask() {
    const topic = yield select(state => state.slip.lastTopic);
    if (topic) {
        updateTask = yield spawn(updatePriceSaga, { topic });
    }
}
export function* editSelectionsWatcher() {
    yield takeEvery(constants.BYO_EDIT_SELECTIONS, editSelectionsSaga);
}
export function* removeSelectionWatcher() {
    yield takeEvery(constants.BYO_REMOVE_SELECTION, removeSelectionSaga);
}
export function* addSelectionWatcher() {
    yield takeEvery(constants.BYO_ADD_SELECTION, addSelectionSaga);
}
export function* startPriceUpdateSubscription() {
    yield takeEvery(constants.BYO_SLIP_START_PRICE_UPDATE_SUBSCRIPTION, enableUpdateTask);
}
export function* stopPriceUpdateSubscription() {
    yield takeEvery(constants.BYO_SLIP_STOP_PRICE_UPDATE_SUBSCRIPTION, cancelUpdateTask);
}
export function* clearAllWatcher() {
    yield takeEvery(constants.BYO_SLIP_CLEAR_ALL, clearAllSaga);
}
export function* callPriceWatcher() {
    const channelQueue = yield actionChannel(constants.BYO_MARKETS_CALL_PRICE);
    while (true) {
        const action = yield take(channelQueue);
        const { selections, eventId, force } = action.payload;
        yield call(callPrice, selections, eventId, force);
    }
}
const slipSagas = [
    fork(addSelectionWatcher),
    fork(removeSelectionWatcher),
    fork(processSelectionWatcher),
    fork(editSelectionsWatcher),
    fork(startPriceUpdateSubscription),
    fork(stopPriceUpdateSubscription),
    fork(clearAllWatcher),
    fork(callPriceWatcher),
];
export default slipSagas;
